import cn from 'classnames';
import gtag from '../../analytics/gtag';
import type { UserData} from '../../api/product';
import { removeEmailReminder } from '../../api/product';
import ErrorBox from '../../generic/ErrorBox';
import Icon from '../../generic/Icon';
import { EmailReminderModal } from '../../generic/Modals';
import showConfirmDialog from '../../generic/showConfirmDialog';
import useUser from '../../login/userContext';
import preventDefault from '../../utils/preventDefault';
import { useAsyncAction } from '../../utils/useAsyncAction';
import type { DetailedProduct } from '../productdetails/ProductDetailsPage';
import * as style from './ProductEmailReminder.module.css';

export interface Props {
    product: DetailedProduct;
    statusNotificationRegistered: boolean | undefined;
    onUserDataChange(userData: UserData): void;
}

export default function ProductEmailReminder({ product, statusNotificationRegistered, onUserDataChange }: Props) {
    if (!product.statusNotification) return null;

    return statusNotificationRegistered ? (
        <RemoveNotification product={product} onUserDataChange={onUserDataChange} />
    ) : (
        <AddNotification product={product} onUserDataChange={onUserDataChange} />
    );
}
function AddNotification({ product, onUserDataChange }: Omit<Props, 'statusNotificationRegistered'>) {
    const { showLoginModal } = useUser();

    const [onClick, isOpen] = useAsyncAction(async () => {
        gtag('event', 'modal_view', {
            modal_name: 'ProductEmailReminder',
            link_text: 'Send meg en epost hvis varen blir tilgjengelig',
        });

        const userData = await EmailReminderModal.upsert(product);

        if (!userData) return;

        gtag('event', 'modal_view', {
            modal_name: 'ProductEmailReminder',
            link_text: 'Bekreft varsling',
        });

        onUserDataChange(userData);
    });

    return (
        <div className={cn(style.productEmailReminder, 'no-print')}>
            <button type="button" className={style.btnTertiary} onClick={preventDefault(() => showLoginModal() || onClick())} disabled={isOpen}>
                <Icon className="icon-email" />
                <span>Send meg en epost hvis varen blir tilgjengelig</span>
            </button>
        </div>
    );
}
function RemoveNotification({ product, onUserDataChange }: Omit<Props, 'statusNotificationRegistered'>) {
    const [onDelete, isLoading, error] = useAsyncAction(async () => {
        gtag('event', 'modal_view', {
            modal_name: 'ProductEmailReminder',
            link_text: 'Fjern varsling',
        });

        if (await showConfirmDialog({ mainText: 'Ønsker du å slette varsling?', confirmButtonText: 'Slett varsling' })) {
            const data = await removeEmailReminder(product.code);
            onUserDataChange(data);

            gtag('event', 'modal_view', {
                modal_name: 'ProductEmailReminder',
                link_text: 'Slett varsling',
            });
        }
    });

    return (
        <div className={style.productEmailReminder}>
            <ErrorBox errors={error} />
            <div className="info-message">
                <div className="message-text">Du vil få en e-post hvis varen blir tilgjengelig</div>
                <button
                    onClick={preventDefault(onDelete)}
                    type="button"
                    aria-label={`Fjern varsling for ${product.name}`}
                    className="btn-tooltip"
                    disabled={isLoading}
                >
                    <Icon className="icon-delete black" />
                </button>
            </div>
        </div>
    );
}
